import React, { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";

import Container from "@mui/material/Container";
import Icon from "@mui/material/Icon";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import DownloadIcon from "@mui/icons-material/Download";

import NavbarDark from "layouts/sections/navigation/navbars/components/NavbarDark";
import DefaultFooter from "examples/Footers/DefaultFooter";
// import SignIn from "layouts/pages/authentication/sign-in";
import FilledInfoCard from "examples/Cards/InfoCards/FilledInfoCard";

import footerRoutes from "footer.routes";

function Vulnerability() {
  const [vulnerability, setVulnerability] = useState(null);
  const [userRole, setUserRole] = useState(null);
  // const [userEmail, setUserEmail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  const storedToken = localStorage.getItem("jwt");
  const MAX_LINES = 2;
  const MAX_CHARACTERS_PER_LINE = 50;

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch vulnerability
        const vulnRes = await axios.get(`/api/vulnerability-db/id/${id}`);
        setVulnerability(vulnRes.data);

        // If JWT exists, check user role
        if (storedToken) {
          try {
            const decoded = jwtDecode(storedToken);
            console.log("Decoded token:", decoded);

            const userRes = await axios.get(`/api/auth/current-user`, {
              headers: { Authorization: `Bearer ${storedToken}` },
            });

            const { role } = userRes.data;
            setUserRole(role);
          } catch (authErr) {
            console.warn("Invalid or expired token, treating as guest.");
            setUserRole("guest");
          }
        }
      } catch (err) {
        console.error("Error fetching vulnerability:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id, storedToken]);

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this entry?")) return;
    try {
      if (userRole !== "admin") {
        alert("You are not authorized to delete this entry.");
        navigate(`/vulnerability-db`);
        return;
      }

      const deleteResponse = await axios.delete(`/api/vulnerability-db/${id}/delete`, {
        headers: { Authorization: `Bearer ${storedToken}` },
      });
      console.log("Delete response:", deleteResponse.data);
      alert("Entry deleted successfully.");
      navigate(`/vulnerability-db`);
    } catch (error) {
      console.error("Error deleting entry:", error);
      alert("Failed to delete the entry. Please try again.");
    }
  };

  const toggleExpandText = () => {
    setIsExpanded((prev) => !prev);
  };

  const getTruncatedDescription = (description) => {
    if (description) {
      const maxCharacters = MAX_LINES * MAX_CHARACTERS_PER_LINE;
      if (description.length > maxCharacters) {
        return description.slice(0, maxCharacters) + "...";
      }
    }
    return description;
  };

  const downloadAttachment = (vulnid, filename) => {
    fetch(`/api/vulnerability-db/attachments/${vulnid}/${filename}`, {
      headers: { Authorization: `Bearer ${storedToken}` },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch((error) => console.error("Error downloading the file:", error));
  };

  if (isLoading) return <div>Loading...</div>;
  if (!vulnerability) return <div>Vulnerability not found.</div>;

  const validAttachments = Array.isArray(vulnerability.attachments)
    ? vulnerability.attachments.filter((a) => a && a.filename)
    : [];

  // const canEditOrDelete =
  //   userRole === "admin" || vulnerability.reporterEmail === userEmail;

  return (
    <>
      <NavbarDark transparent light />
      <Container sx={{ mt: 4, maxWidth: "100%" }}>
        <Grid container spacing={0} alignItems="center">
          <Grid item xs={12}>
            <MKTypography variant="h3" my={1}>
              {vulnerability.title}
            </MKTypography>
            <MKTypography variant="body2" color="text" mb={2}>
              {/* Conditionally show either truncated or full description */}
              {isExpanded
                ? vulnerability.report_description
                : getTruncatedDescription(vulnerability.report_description)}
            </MKTypography>
            {/* Show "See more" link only if description has more than MAX_LINES */}
            {vulnerability.report_description &&
              vulnerability.report_description.length > MAX_LINES * MAX_CHARACTERS_PER_LINE && (
                <MKTypography
                  component="a"
                  href="#"
                  variant="body2"
                  color="info"
                  fontWeight="regular"
                  onClick={(e) => {
                    e.preventDefault();
                    toggleExpandText();
                  }}
                  sx={{
                    width: "max-content",
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    "& .material-icons-round": {
                      fontSize: "1.125rem",
                      transform: "translateX(3px)",
                      transition: "transform 0.2s cubic-bezier(0.34, 1.61, 0.7, 1.3)",
                    },
                    "&:hover .material-icons-round, &:focus .material-icons-round": {
                      transform: "translateX(6px)",
                    },
                  }}
                >
                  {isExpanded ? "See less..." : "See more..."}
                  <Icon sx={{ fontWeight: "bold" }}>
                    {isExpanded ? "arrow_upward" : "arrow_forward"}
                  </Icon>
                </MKTypography>
              )}
          </Grid>
        </Grid>
        <Grid
          item
          spacing={0}
          xs={12}
          lg={6}
          sx={{ ml: { xs: -2, lg: "auto" }, mt: { xs: 6, lg: 0 } }}
        >
          <Stack>
            <MKBox display="flex" alignItems="flex-start" p={2}>
              <MKBox
                width="3rem"
                height="3rem"
                minWidth="3rem"
                minHeight="3rem"
                variant="gradient"
                bgColor="info"
                color="white"
                coloredShadow="info"
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="xl"
              >
                <Icon fontSize="small">mediation</Icon>
              </MKBox>
              <MKBox pl={2}>
                <MKTypography variant="body1" color="text">
                  Vulnerability location in lifecycle
                </MKTypography>
                <MKTypography variant="body2" color="bold">
                  {vulnerability.phase}
                </MKTypography>
                <MKTypography variant="body2" color="text">
                  {vulnerability.phaseDescription}
                </MKTypography>
              </MKBox>
            </MKBox>

            <MKBox display="flex" alignItems="flex-start" p={2}>
              <MKBox
                width="3rem"
                height="3rem"
                minWidth="3rem"
                minHeight="3rem"
                variant="gradient"
                bgColor="info"
                color="white"
                coloredShadow="info"
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="xl"
              >
                <Icon fontSize="small">settings_overscan</Icon>
              </MKBox>
              <MKBox pl={2}>
                <MKTypography variant="body1" color="text">
                  Potentially compromised attributes
                </MKTypography>
                <MKTypography variant="body2" color="bold">
                  {vulnerability.attributeName}
                </MKTypography>
                <MKTypography variant="body2" color="text">
                  {vulnerability.attr_Description}
                </MKTypography>
              </MKBox>
            </MKBox>

            <MKBox display="flex" alignItems="flex-start" p={2}>
              <MKBox
                width="3rem"
                height="3rem"
                minWidth="3rem"
                minHeight="3rem"
                variant="gradient"
                bgColor="info"
                color="white"
                coloredShadow="info"
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="xl"
              >
                <Icon fontSize="small">token</Icon>
              </MKBox>
              <MKBox pl={2}>
                <MKTypography variant="body1" color="text">
                  Possible effect of exploitation
                </MKTypography>
                <MKTypography variant="body2" color="bold">
                  {vulnerability.effectName}
                </MKTypography>
                <MKTypography variant="body2" color="text">
                  {vulnerability.eff_Description}
                </MKTypography>
              </MKBox>
            </MKBox>
          </Stack>
        </Grid>
        <Grid container spacing={2} justifyContent="center">
          <Grid item xs={12} md={4}>
            <FilledInfoCard
              color="info"
              icon="apps"
              title="Artifact Type"
              description={vulnerability.artifactType}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FilledInfoCard
              color="info"
              icon="apps"
              title="Recorded date"
              description={vulnerability.date_added?.slice(0, 10)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FilledInfoCard
              color="info"
              icon="apps"
              title="Last updated date"
              description={vulnerability.last_updated?.slice(0, 10)}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} justifyContent="left">
          <Grid item xs={12} sm={6} md={4}>
            {vulnerability.reporterOrganization && (
              <MKBox
                p={2}
                sx={{
                  textAlign: "left",
                }}
              >
                <MKTypography variant="body1">Report submitted by:</MKTypography>
                <MKTypography variant="body2">{vulnerability.reporterOrganization}</MKTypography>
              </MKBox>
            )}
            {vulnerability.source && (
              <MKBox
                p={2}
                sx={{
                  textAlign: "left",
                }}
              >
                <MKTypography variant="body1">Report source:</MKTypography>
                <MKTypography variant="body2">{vulnerability.source}</MKTypography>
              </MKBox>
            )}
            {vulnerability.developer && (
              <MKBox
                p={2}
                sx={{
                  textAlign: "left",
                }}
              >
                <MKTypography variant="body1">Developer of the system: </MKTypography>
                <MKTypography variant="body2">{vulnerability.developer}</MKTypography>
              </MKBox>
            )}
            {vulnerability.deployer && (
              <MKBox
                p={2}
                sx={{
                  textAlign: "left",
                }}
              >
                <MKTypography variant="body1">Deployer of the system: </MKTypography>
                <MKTypography variant="body2">{vulnerability.deployer}</MKTypography>
              </MKBox>
            )}
            {vulnerability.cve_link && (
              <MKBox
                p={2}
                sx={{
                  textAlign: "left",
                }}
              >
                <MKTypography variant="body1">CVE Link: </MKTypography>
                <MKTypography variant="body2">
                  Access the CVE details{" "}
                  <a href={vulnerability.cve_link} target="_blank" rel="noopener noreferrer">
                    here..
                  </a>
                </MKTypography>
              </MKBox>
            )}
            {validAttachments.length > 0 ? (
              <MKBox component="table" width="100%" sx={{ mt: 2 }}>
                <tbody>
                  {validAttachments.map((attachment, index) => (
                    <tr key={index}>
                      <td>
                        <MKButton
                          variant="text"
                          color="info"
                          startIcon={<DownloadIcon />}
                          onClick={() => downloadAttachment(vulnerability.id, attachment.filename)}
                          sx={{
                            textTransform: "none",
                            fontWeight: "normal",
                            justifyContent: "flex-start",
                            minWidth: 200,
                            "&:hover": {
                              backgroundColor: "rgba(0,0,0,0.04)",
                            },
                          }}
                        >
                          {attachment.filename}
                        </MKButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </MKBox>
            ) : (
              <MKTypography variant="body2" color="textSecondary" mt={1}>
                No attachments available.
              </MKTypography>
            )}
          </Grid>
        </Grid>
        <Grid
          container
          spacing={2}
          justifyContent="flex-end"
          alignItems="center"
          sx={{ textAlign: "center" }}
        >
          {userRole === "admin" && (
            <Grid item>
              <MKButton
                component={Link}
                to={`/vulnerability-db/editid/${id}`}
                sx={{
                  padding: 2,
                  textAlign: "center",
                }}
              >
                Edit Details
              </MKButton>
            </Grid>
          )}
          {userRole === "admin" && (
            <Grid item>
              <MKButton
                onClick={handleDelete}
                sx={{
                  padding: 2,
                  textAlign: "center",
                }}
              >
                Delete Entry
              </MKButton>
            </Grid>
          )}
          {userRole !== "admin" && (
            <MKTypography variant="body2" color="textSecondary" mt={2}>
              <em>Login to edit or report vulnerabilities.</em>
            </MKTypography>
          )}
        </Grid>
      </Container>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}
export default Vulnerability;
